import { createSlice } from "@reduxjs/toolkit";
import ApiClientConnector from "src/api/ApiClientConnector";
import { convertToSummaries, updateGuest } from "../actions/ReservationSummaryActions";
import { transformToSummaryModels } from "../actions/ViewReservationSummaryActions";
import { convertToOverview } from "../actions/ReservationOverviewActions";
import { convertToClientVoucher } from "../actions/ReservationClientVoucherActions";

const initialState = {
    reservationSummaryList: [],
    backOfficeReservationSummaryList: [],
    reservationOverview: [],
    reservationId: '',
    reservationError: {},
    isGuestInfoChanged: false,
    isDetailView: false,
    selectedItem: null,
    clientVoucherDetails: [],
    clientVoucherDetailsList: [],
    entityMessageRequest: [],
    entityMessageResponse: [],
    syncResponse: null,
    isConfirmationReservation: false,
};

export const ReservationsManagerSlice = createSlice({
    name: 'reservations',
    initialState,
    reducers: {
        setReservationSummaryList: (state, action) => {
            state.reservationSummaryList = action.payload;
        },
        setBackOfficeReservationSummaryList: (state, action) => {
            state.backOfficeReservationSummaryList = action.payload;
        },
        setReservationId: (state, action) => {
            state.reservationId = action.payload;
        },
        setEntityMessageRequest: (state, action) => {//Entity can be Hotel or Service
            state.entityMessageRequest = action.payload;
        },
        clearEntityMessageRequest: (state) => {
            state.entityMessageRequest = [];
        },
        setEntityMessageResponse: (state, action) => {
            state.entityMessageResponse = action.payload;
        },
        clearEntityMessageResponse: (state) => {
            state.entityMessageResponse = [];
        },
        setReservationOverview: (state, action) => {
            state.reservationOverview = action.payload;
        },
        setGuestInfoChanged: (state, action) => {
            state.isGuestInfoChanged = action.payload;
        },
        setConfirmationNo: (state, action) => {
            const { itemReference, confirmationNo } = action.payload;

            if (state.reservationOverview) {
                const { Hotels = [], Services = [] } = state.reservationOverview;

                // Find hotel with matching ItemReference
                const hotel = Hotels.find(h => h.ItemReference === itemReference);
                if (hotel) {
                    hotel.ConfirmationNo = confirmationNo;
                } else {
                    // Find service with matching ItemReference
                    const service = Services.find(s => s.ItemReference === itemReference);
                    if (service) {
                        service.ConfirmationNo = confirmationNo;
                    }
                }
            }
        },

        setStatus: (state, action) => {
            const { status, cancellationNote } = action.payload;
            if (state.reservationOverview && state.reservationOverview.Hotels) {
                const updatedOverview = {
                    ...state.reservationOverview,
                    Status: status,
                    CancellationNote: cancellationNote
                };
                state.reservationOverview = updatedOverview;
            }
        },

        setItemStatus: (state, action) => {
            const { itemReference, status } = action.payload;

            if (!state.reservationOverview?.Hotels) return;

            const hotelIndex = state.reservationOverview.Hotels.findIndex(
                hotel => hotel.ItemReference === itemReference
            );

            if (hotelIndex !== -1) {
                state.reservationOverview.Hotels[hotelIndex].Status = status;
            }
        },

        setAllItemsStatus: (state, action) => {
            const { status } = action.payload;

            if (state.reservationOverview) {
                // Update all hotels
                if (state.reservationOverview.Hotels) {
                    state.reservationOverview.Hotels.forEach(hotel => {
                        hotel.Status = status;
                    });
                }

                // Update all services
                if (state.reservationOverview.Services) {
                    state.reservationOverview.Services.forEach(service => {
                        service.Status = status;
                    });
                }
            }
        },

        setItemReferenceStatus: (state, action) => {
            const { value, itemReference } = action.payload;

            if (state.reservationOverview) {
                const { Hotels = [], Services = [] } = state.reservationOverview;

                // Find hotel with matching ItemReference
                const hotel = Hotels.find(h => h.ItemReference === itemReference);
                if (hotel) {
                    hotel.Status = value;
                } else {
                    // Find service with matching ItemReference
                    const service = Services.find(s => s.ItemReference === itemReference);
                    if (service) {
                        service.Status = value;
                    }
                }
            }
        },

        changeDetailView: (state, action) => {
            state.isDetailView = action.payload;
        },
        setSelectedItem: (state, action) => {
            state.selectedItem = action.payload;
        },
        setClientVoucher: (state, action) => {
            state.clientVoucherDetails = action.payload;
        },
        setClientVoucherDetailsList: (state, action) => {
            state.clientVoucherDetailsList = action.payload;
        },

        updateGuestTitle: (state, action) => {
            const { guest: originalGuest, title } = action.payload;
            updateGuest(state, originalGuest, guest => {
                guest.Title = title;
            });
        },

        setConfirmationReservation: (state, action) => {
            state.isConfirmationReservation = action.payload;
        },

        updateGuestGender: (state, action) => {
            const { guest: originalGuest, gender } = action.payload;
            updateGuest(state, originalGuest, guest => {
                guest.Gender = gender;
            });
        },

        updateGuestFirstName: (state, action) => {
            const { guest: originalGuest, firstName } = action.payload;
            updateGuest(state, originalGuest, guest => {
                guest.FirstName = firstName;
            });
        },

        updateGuestLastName: (state, action) => {
            const { guest: originalGuest, lastName } = action.payload;
            updateGuest(state, originalGuest, guest => {
                guest.LastName = lastName;
            });
        },

        updateGuestDateOfBirth: (state, action) => {
            const { guest: originalGuest, dateOfBirth } = action.payload;
            updateGuest(state, originalGuest, guest => {
                guest.DateOfBirth = dateOfBirth;
            });
        },

        updateGuestCountry: (state, action) => {
            const { guest: originalGuest, country } = action.payload;
            updateGuest(state, originalGuest, guest => {
                guest.Country = country;
                guest.CountryCode = country?.Code || "";
            });
        },

        updateAllGuestCountries: (state, action) => {
            const { country } = action.payload;
            if (state.reservationOverview?.Pax) {
                state.reservationOverview.Pax.forEach(guest => {
                    guest.Country = country;
                    guest.CountryCode = country?.Code || "";
                });
                state.isGuestInfoChanged = true;
            }
        },

        updateGuestLeadStatus: (state, action) => {
            const { guest: originalGuest, isLead } = action.payload;
            if (state.reservationOverview?.Pax) {
                if (isLead) {
                    state.reservationOverview.Pax.forEach(p => p.LeadGuest = false);
                }

                updateGuest(state, originalGuest, guest => {
                    guest.LeadGuest = isLead;
                });
            }
        },

        setSyncResponse: (state, action) => {
            state.syncResponse = action.payload;
        },
    }
});

export const {
    setReservationSummaryList,
    setBackOfficeReservationSummaryList, setReservationId,
    setReservationOverview,
    setGuestInfoChanged,
    changeDetailView,
    setSelectedItem,
    setClientVoucher, setConfirmationNo, setStatus, setItemStatus, setAllItemsStatus,
    setItemReferenceStatus,
    updateGuestTitle,
    updateGuestFirstName,
    updateGuestLastName,
    updateGuestDateOfBirth,
    updateGuestCountry,
    updateGuestLeadStatus,
    updateAllGuestCountries,
    setClientVoucherDetailsList,
    setSyncResponse,
    setConfirmationReservation,
    updateGuestGender,
    setEntityMessageResponse,
    setEntityMessageRequest,
    clearEntityMessageRequest,
    clearEntityMessageResponse
} = ReservationsManagerSlice.actions;

export const fetchReservationSummaryList = (instance, accounts, criteria) => async (dispatch) => {
    const client = await ApiClientConnector(instance, accounts);
    const result = await client.reservations.get(criteria);
    const reservations = convertToSummaries(result);
    dispatch(setReservationSummaryList(reservations));
    dispatch(setSelectedItem(reservations));
    return result;

}

export const fetchBackOfficeReservationSummaryList = (instance, accounts, criteria) => async (dispatch) => {
    const client = await ApiClientConnector(instance, accounts);
    const result = await client.backOfficeReservations.get(criteria);
    const reservations = transformToSummaryModels(result);
    dispatch(setBackOfficeReservationSummaryList(reservations));
    return result;
}

export const fetchBackOfficeReservationById = (instance, accounts, id) => async (dispatch) => {
    const client = await ApiClientConnector(instance, accounts);
    const result = await client.backOfficeReservations.getById(id);
    const reservations = convertToOverview(result);
    dispatch(setReservationOverview(reservations));
    return reservations;
};

export const fetchBackOfficeConfirmationNumber = (instance, accounts, id, provider) => async (dispatch) => {
    const client = await ApiClientConnector(instance, accounts);
    const result = await client.backOfficeReservations.getConfirmationNumber(id, provider);
    dispatch(setConfirmationNo({ index: 0, confirmationNo: result.SupplierConfirmation }));
    return result;
};

export const fetchReservationById = (instance, accounts, id) => async (dispatch) => {
    const client = await ApiClientConnector(instance, accounts);
    const result = await client.reservations.getById(id);
    const reservations = convertToOverview(result);
    dispatch(setReservationOverview(reservations));
    return reservations;
};

export const updateReservationGuestInformation = (instance, accounts, id, payload) => async (dispatch) => {
    const client = await ApiClientConnector(instance, accounts);
    const result = await client.reservations.updateGuestInfo(id, payload);

    dispatch(setSelectedItem(result));
};

export const updateSupplierHotelConfirmation = (instance, accounts, id, propertyId, payload) => async (dispatch) => {
    const client = await ApiClientConnector(instance, accounts);
    const result = await client.backOfficeReservations.updateHotelConfirmation(id, propertyId, payload);
    dispatch(setSelectedItem(result));
};

export const updateSupplierServiceConfirmation = (instance, accounts, id, serviceId, payload) => async (dispatch) => {
    const client = await ApiClientConnector(instance, accounts);
    const result = await client.backOfficeReservations.updateServiceConfirmation(id, serviceId, payload);
    dispatch(setSelectedItem(result));
};

export const updateSupplierStatus = (instance, accounts, id, payload) => async () => {
    const client = await ApiClientConnector(instance, accounts);
    await client.backOfficeReservations.updateStatus(id, payload);
};

export const updateHotelMarkUpBuyRate = (instance, accounts, id, propertyId, payload) => async (dispatch) => {
    const client = await ApiClientConnector(instance, accounts);
    const result = await client.backOfficeReservations.updateMarkUpHotelRate(id, propertyId, payload);
    const reservations = convertToOverview(result);
    dispatch(setReservationOverview(reservations));
};

export const updateServiceMarkUpBuyRate = (instance, accounts, id, propertyId, payload) => async (dispatch) => {
    const client = await ApiClientConnector(instance, accounts);
    const result = await client.backOfficeReservations.updateMarkUpServiceRate(id, propertyId, payload);
    const reservations = convertToOverview(result);
    dispatch(setReservationOverview(reservations));
};

export const updateHotelMarkUpSellTotal = (instance, accounts, id, propertyId, payload) => async (dispatch) => {
    const client = await ApiClientConnector(instance, accounts);
    const result = await client.backOfficeReservations.updateMarkUpHotelRate(id, propertyId, payload);
    const reservations = convertToOverview(result);
    dispatch(setReservationOverview(reservations));
};

export const updateServiceMarkUpSellTotal = (instance, accounts, id, propertyId, payload) => async (dispatch) => {
    const client = await ApiClientConnector(instance, accounts);
    const result = await client.backOfficeReservations.updateMarkUpServiceRate(id, propertyId, payload);
    const reservations = convertToOverview(result);
    dispatch(setReservationOverview(reservations));
};

export const updateSupplierHotelStatus = (instance, accounts, id, payload) => async (dispatch) => {
    const client = await ApiClientConnector(instance, accounts);
    const result = await client.backOfficeReservations.updateHotelStatus(id, payload);
    dispatch(setSelectedItem(result));
};

export const updateSupplierServiceStatus = (instance, accounts, id, payload) => async (dispatch) => {
    const client = await ApiClientConnector(instance, accounts);
    const result = await client.backOfficeReservations.updateServiceStatus(id, payload);
    dispatch(setSelectedItem(result));
};

export const generateClientVoucher = (instance, accounts, reservationId) => async (dispatch) => {
    const client = await ApiClientConnector(instance, accounts);
    const result = await client.reservations.generateClientVoucher(reservationId);
    const clientVoucherDetails = convertToClientVoucher(result)
    dispatch(setClientVoucher(clientVoucherDetails));
    return result;
};

export const fetchClientVoucherDetailsList = (instance, accounts, id) => async (dispatch) => {
    const client = await ApiClientConnector(instance, accounts);
    const result = await client.reservations.getClientVoucherList(id);
    dispatch(setClientVoucherDetailsList(result));
    return result;
};

export const fetchGeneratedAllClientVoucherDetails = (instance, accounts, reservationId, voucherId) => async (dispatch) => {
    const client = await ApiClientConnector(instance, accounts);
    const result = await client.reservations.getGeneratedAllClientVoucher(reservationId, voucherId);
    const clientVoucherDetails = convertToClientVoucher(result)
    dispatch(setClientVoucher(clientVoucherDetails));
    return result;
};

export const fetchGeneratedSingleClientVoucherDetails = (instance, accounts, reservationId, voucherId, itemReference) => async (dispatch) => {
    const client = await ApiClientConnector(instance, accounts);
    const result = await client.reservations.getGeneratedSingleClientVoucher(reservationId, voucherId, itemReference);
    const clientVoucherDetails = result ? convertToClientVoucher(result) : convertToClientVoucher([]); // empty client voucher reducer if there are no results (handle error : when fetching client voucher by item reference, if there is no result, should return error without displaying current client voucher list)
    dispatch(setClientVoucher(clientVoucherDetails));
    return result;
};

export const syncReservations = (instance, accounts, reservationId) => async (dispatch) => {
    const client = await ApiClientConnector(instance, accounts);
    const result = await client.reservations.syncReservations(reservationId);
    dispatch(setSyncResponse(result));
    return result;
};

export const saveServiceReservations = (instance, accounts, criteria) => async (dispatch) => {
    const client = await ApiClientConnector(instance, accounts);
    const result = await client.reservations.createService(criteria);
    return result.ReservationId;
};

export const entityMessageAvailability = (instance, accounts, criteria) => async (dispatch) => {
    const client = await ApiClientConnector(instance, accounts);
    const result = await client.reservations.hotelAvailability(criteria);
    dispatch(setEntityMessageResponse(result));
    return result;
};

export const createReservations = (instance, accounts, criteria) => async (dispatch) => {
    const client = await ApiClientConnector(instance, accounts);
    const result = await client.reservations.reserve(criteria);
    return result.ReservationId;
};

export const cancelReservation = (instance, accounts, reservationId, criteria) => async (dispatch) => {
    const client = await ApiClientConnector(instance, accounts);
    const result = await client.reservations.removeEntireReservation(reservationId, criteria);
    dispatch(setStatus({ status: result.Status, cancellationNote: result.CancellationNote }));
    if (result.CancelledItems && result.CancelledItems.length > 0) {
        result.CancelledItems.forEach(item => {
            dispatch(setItemStatus({
                itemReference: item.ItemReference,
                status: item.Status
            }));
        });
    }
    return result;
};

export default ReservationsManagerSlice.reducer;